import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import "@fontsource-variable/noto-sans-thai";
import "@/styles/app.css";
import { I18nProvider } from "@/i18n/I18nProvider";
import { APIError, setAuthFailureHandler, setCSRF } from "@/lib/api";
import { logout } from "@/lib/auth";
import { createSessionController } from "@/lib/session";
import { recordNavigation } from "@/lib/telemetry";

import { routeTree } from "./routeTree.gen";
import { PendingScreen } from "@/components/layout/PendingScreen";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: (count, err) => {
        // A refusal is an answer, not a blip: never retry 4xx.
        const status = err instanceof APIError ? err.status : (err as { status?: number }).status;
        if (status !== undefined && status >= 400 && status < 500) return false;
        return count < 2;
      },
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    // Commands are never retried automatically — a money command's retry
    // goes through its own dialog with the same Idempotency-Key.
    mutations: { retry: false },
  },
});

const session = createSessionController({
  clearQueries: () => queryClient.clear(),
  clearCsrf: () => setCSRF(null),
  serverLogout: logout,
  redirectToLogin: (reason) => {
    void router.navigate({ to: "/login", search: { reason }, replace: true });
  },
});
// A 401 on any call ends the session: no protected figure survives it.
setAuthFailureHandler((err) => session.onApiError(err));

const router = createRouter({
  routeTree,
  context: { queryClient, session },
  defaultPreload: "intent",
  scrollRestoration: true,
  // Never a blank page while the shell's guards run.
  defaultPendingComponent: PendingScreen,
  defaultPendingMs: 150,
  // The shell re-reads memberships on navigation; do not let a preload
  // cache a guard decision for longer than a moment.
  defaultPreloadStaleTime: 0,
});

// Audit-safe navigation telemetry: the matched route TEMPLATE and how long
// the navigation took — never the URL (it carries node ids).
let navStarted = 0;
router.subscribe("onBeforeNavigate", () => {
  navStarted = Date.now();
});
router.subscribe("onResolved", () => {
  const matches = router.state.matches;
  const leaf = matches[matches.length - 1];
  if (leaf && navStarted) recordNavigation({ route: leaf.routeId, latencyMs: Date.now() - navStarted });
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <I18nProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </I18nProvider>
  </React.StrictMode>,
);
